import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Checkbox,
  TextField,
  Box,
  LinearProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";

const MediaSelectionDialog = ({
  open,
  onClose,
  userId,
  notice,
  selectedRoleId,
  selectedRoleName,
  existingSubmission,
  isEdit,
  onSubmitSuccess,
  onSubmitError,
}) => {
  const [mediaItems, setMediaItems] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [coverMsg, setCoverMsg] = useState("");
  const [additionalLink, setAdditionalLink] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState("");
  const [existingMediaURL, setExistingMediaURL] = useState("");

  const email = localStorage.getItem("email");
  const stored = localStorage.getItem("loggedInUser");

  let userDetails = null;
  if (stored) {
    try {
      userDetails = JSON.parse(stored);
    } catch {}
  }

  const fullName =
    `${userDetails?.data?.userFirstName || ""} ${userDetails?.data?.userLastName || ""}`.trim();

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await api.get(ENDPOINTS.getUserMedia({ userId }));
      const items = Array.isArray(res.data) ? res.data : [];

      const photos = items
        .filter((m) => {
          const typeOk = ["picture", "image", "profile"].includes(
            m.mediaType?.toLowerCase()
          );
          const idOk = m.mediaId !== null && m.mediaId !== undefined;
          const urlOk = !!m.mediaName && m.mediaName.startsWith("http");
          return typeOk && idOk && urlOk;
        })
        .sort((a, b) => {
          if (a.mediaType === "PROFILE") return -1;
          if (b.mediaType === "PROFILE") return 1;
          return (a.picSort || 0) - (b.picSort || 0);
        });

      setMediaItems(photos);
    } catch {
      setMediaItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Effect 1: fetch media on open
  useEffect(() => {
    if (!open) return;
    fetchMedia();
    setFileError("");
    setUploadProgress(0);
  }, [open, userId]);

  // Effect 2: reset all state when opening for a NEW submission
  useEffect(() => {
    if (!open || isEdit) return;
    setSelectedMedia(null);
    setUploadedFile(null);
    setUploadedPreview(null);
    setCoverMsg("");
    setAdditionalLink("");
  }, [open, isEdit]);

  // Effect 3: prefill all fields for EDIT — re-runs when mediaItems load
  useEffect(() => {
    if (!open || !isEdit || !existingSubmission) return;

    // Always set text fields immediately (don't wait for mediaItems)
    setCoverMsg(existingSubmission.coverMessage || "");
    setAdditionalLink(existingSubmission.videoPath || "");

    // Wait for mediaItems before doing media matching
    if (mediaItems.length === 0) return;

    // --- Gallery photo: match using imageURL (added by backend) ---
    const imageURL = existingSubmission.imageURL;
    const hasValidImageURL = imageURL && imageURL !== "null" && imageURL !== "";

    if (hasValidImageURL) {
      const matched = mediaItems.find((m) => m.mediaName === imageURL);
      setSelectedMedia(matched || null);
    } else {
      setSelectedMedia(null);
    }

    // --- Uploaded file preview: use mediaURL (independent of gallery selection) ---
    const mediaURL = existingSubmission.mediaURL;
const hasValidMediaURL = mediaURL && mediaURL !== "null" && mediaURL !== "";

if (hasValidMediaURL) {
  setUploadedPreview(mediaURL);
  setExistingMediaURL(mediaURL);
  setUploadedFile(null);
} else {
  setUploadedPreview(null);
  setExistingMediaURL("");
}
  }, [open, isEdit, existingSubmission, mediaItems]);

  const handleFilePick = (file) => {
    if (!file) return;
    const MAX_SIZE = 500 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setFileError("This file is too large. Please upload a file under 500 MB.");
      setUploadedFile(null);
      setUploadedPreview(null);
      return;
    }
    setFileError("");
    setUploadedFile(file);
    setUploadedPreview(URL.createObjectURL(file));
  };

 const handleRemoveFile = () => {
  setUploadedFile(null);
  setUploadedPreview(null);
  setExistingMediaURL("");
  setFileError("");
};

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append("model.userID", Number(userId));
    formData.append("model.type", 2);
    formData.append("model.noticeID", notice.noticeId);
    formData.append("model.from", email || "");
    formData.append("model.to", notice.noticeSubmittedByEmail || "");
    formData.append("model.noticeTitle", notice.title || "");
    formData.append("model.coverMsg", coverMsg || "");
    formData.append("model.htmlFormat", 1);
    formData.append("model.roleId", selectedRoleId || 0);
    formData.append("model.imageURL", selectedMedia?.mediaName || "");
if (uploadedFile) {
  // User uploaded NEW file
  formData.append("model.MediaFile", uploadedFile);
  formData.append("model.MediaURL", "");
} else if (existingMediaURL) {
  // User kept the OLD file
  formData.append("model.MediaURL", existingMediaURL);
} else {
  // User removed file
  formData.append("model.MediaURL", "");
}
    formData.append("model.videoPath", additionalLink || "");
    formData.append("model.talentName", fullName || "");
    formData.append("model.directorName", notice.directorName || "");
    formData.append("model.submissionLink", ENDPOINTS.submitJob());

    if (uploadedFile) {
      formData.append("model.MediaFile", uploadedFile);
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const res = await api.post(ENDPOINTS.submitJob(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percent);
        },
      });

      const message =
        res?.data?.message?.result ||
        res?.data?.message ||
        "Submitted Successfully";

      setUploading(false);
      setUploadProgress(0);
      onSubmitSuccess?.(message);
      onClose();
    } catch (e) {
      setUploading(false);
      setUploadProgress(0);
      onSubmitError?.(e.response?.data?.message || "Submission Failed");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={uploading ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ backgroundColor: "#202020", color: "#fff" }}>
        {isEdit ? "Edit Submission for:" : "DirectSubmit to:"}{" "}
        {selectedRoleName || "Role"}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          backgroundColor: "#202020",
          p: 0,
          display: "flex",
          flexDirection: "column",
          height: "80vh",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Instructions */}
            <Box px={2} pt={0} pb={0} sx={{ flexShrink: 0 }}>
              <Typography sx={{ color: "#fff" }} fontSize="1rem">
                To submit yourself to this role: 1) choose the photo you want
                the casting director to see first. 2) If requested, add any
                other media. 3) write a cover note. 4) Click the DirectSubmit
                Now button.
              </Typography>
              <Typography sx={{ color: "#90b5fe" }} fontSize="0.85rem" mt={0.5}>
                Please upload files smaller than 500MB.
              </Typography>
            </Box>

            {/* Photo Gallery — scrollable */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                minHeight: 0,
                "&::-webkit-scrollbar": { width: "8px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#90b5fe",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#7da6f9",
                },
                scrollbarWidth: "thin",
                scrollbarColor: "#90b5fe transparent",
              }}
            >
              <Grid container spacing={2}>
                {mediaItems.length === 0 && (
                  <Grid item xs={12}>
                    <Typography sx={{ color: "#fff" }}>
                      No photos available.
                    </Typography>
                  </Grid>
                )}
                {mediaItems.map((media) => (
                  <Grid item xs={12} sm={6} md={3} key={media.mediaId}>
                    <Box
                      border={1}
                      borderRadius={2}
                      borderColor={
                        selectedMedia?.mediaId === media.mediaId
                          ? "#90b5fe"
                          : "#ccc"
                      }
                      p={1}
                      sx={{
                        position: "relative",
                        cursor: "pointer",
                        outline:
                          selectedMedia?.mediaId === media.mediaId
                            ? "2px solid #90b5fe"
                            : "none",
                      }}
                      onClick={() => setSelectedMedia(media)}
                    >
                      <Checkbox
                        checked={selectedMedia?.mediaId === media.mediaId}
                        onChange={() => setSelectedMedia(media)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          left: 4,
                          zIndex: 2,
                        }}
                      />
                      <img
                        src={media.mediaName}
                        alt="Photo"
                        onError={(e) => (e.target.style.display = "none")}
                        style={{
                          width: "100%",
                          height: 180,
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Bottom fixed section */}
            <Box sx={{ flexShrink: 0, borderTop: "1px solid #555" }}>
              {/* File upload button */}
              <Box px={2} pt={2}>
                <Button
                  variant="outlined"
                  sx={{ width: "100%", background: "#fff", color: "#000" }}
                  component="label"
                  disabled={uploading}
                >
                  Upload requested Self-tapes, Audio or Photo here
                  <input
                    hidden
                    type="file"
                    accept="image/*,video/*,audio/*"
                    onChange={(e) => handleFilePick(e.target.files[0])}
                  />
                </Button>

                {fileError && (
                  <Typography color="error" fontSize="0.85rem" mt={0.5}>
                    {fileError}
                  </Typography>
                )}
              </Box>

              {/* File preview — works for both newly picked files and existing submission URLs */}
              {uploadedPreview && (
                <Box
                  px={2}
                  pt={1}
                  pb={0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  {(() => {
                    const isImage = uploadedFile
                      ? uploadedFile.type.startsWith("image")
                      : /\.(jpg|jpeg|png|gif|webp)$/i.test(uploadedPreview);
                    const isVideo = uploadedFile
                      ? uploadedFile.type.startsWith("video")
                      : /\.(mp4|mov|avi|webm|mkv)$/i.test(uploadedPreview);
                    const isAudio = uploadedFile
                      ? uploadedFile.type.startsWith("audio")
                      : /\.(mp3|wav|aac|ogg|m4a)$/i.test(uploadedPreview);

                    return (
                      <>
                        {isImage && (
                          <img
                            src={uploadedPreview}
                            alt="preview"
                            style={{
                              width: 60,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: 6,
                            }}
                          />
                        )}
                        {isVideo && (
                          <video
                            src={uploadedPreview}
                            style={{ width: 100, height: 60, borderRadius: 6 }}
                          />
                        )}
                        {isAudio && (
                          <audio
                            src={uploadedPreview}
                            controls
                            style={{ height: 36 }}
                          />
                        )}
                      </>
                    );
                  })()}

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ color: "#fff", fontSize: "0.85rem" }}
                      noWrap
                    >
                      {uploadedFile
                        ? uploadedFile.name
                        : uploadedPreview.split("/").pop()}
                    </Typography>
                    {uploadedFile && (
                      <Typography sx={{ color: "#aaa", fontSize: "0.75rem" }}>
                        {(uploadedFile.size / (1024 * 1024)).toFixed(1)} MB
                      </Typography>
                    )}
                  </Box>

                  <IconButton
                    size="small"
                    onClick={handleRemoveFile}
                    disabled={uploading}
                    sx={{ color: "#f44336" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* Upload progress bar */}
              {uploading && (
                <Box px={2} pt={1}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography sx={{ color: "#fff", fontSize: "0.85rem" }}>
                      Uploading...
                    </Typography>
                    <Typography
                      sx={{
                        color: "#90b5fe",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                      }}
                    >
                      {uploadProgress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{
                      height: 8,
                      borderRadius: 5,
                      backgroundColor: "#555",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#90b5fe",
                      },
                    }}
                  />
                </Box>
              )}

              {/* Cover letter + additional link */}
              <Box
                p={2}
                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
              >
                <TextField
                  fullWidth
                  label="Cover Letter (optional)"
                  multiline
                  rows={2}
                  value={coverMsg}
                  onChange={(e) => setCoverMsg(e.target.value)}
                  disabled={uploading}
                  InputLabelProps={{ style: { color: "#aaa" } }}
                  inputProps={{ style: { color: "#000" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      "& fieldset": { borderColor: "#666" },
                      "&:hover fieldset": { borderColor: "#90b5fe" },
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Additional Link (optional)"
                  placeholder="YouTube, Vimeo, Dropbox, Website etc."
                  value={additionalLink}
                  onChange={(e) => setAdditionalLink(e.target.value)}
                  disabled={uploading}
                  InputLabelProps={{ style: { color: "#aaa" } }}
                  inputProps={{ style: { color: "#000" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      "& fieldset": { borderColor: "#666" },
                      "&:hover fieldset": { borderColor: "#90b5fe" },
                    },
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ backgroundColor: "#202020" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={uploading}
          sx={{ color: "#eee", border: "#eee" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={
  uploading ||
  !!fileError ||
  (!selectedMedia && !uploadedPreview && !existingMediaURL)
}
        >
          {uploading
            ? `Uploading ${uploadProgress}%`
            : isEdit
            ? "Update Submission"
            : "DirectSubmit Now"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaSelectionDialog;